import { useEffect } from 'react';

import { clearAllDatabases } from '@treams/treams-analytics';
import { expectedUserVar, sessionSlotSwitchInFlight } from '@treams/treams-hooks';
import { useSessionSlotsLazyQuery, useSignOutSessionMutation } from '@treams/treams-network';

export default function Logout() {
	const [signOutSession] = useSignOutSessionMutation();
	const [fetchSessionSlots] = useSessionSlotsLazyQuery({ fetchPolicy: 'network-only' });

	useEffect(() => {
		(async () => {
			// Signing out changes (or removes) the active slot server-side;
			// suppress the reload-on-mismatch handling in useHttpLink so
			// concurrent requests can't trigger a competing hard reload that
			// races the cleanup below. Never reset: every path ends in a
			// full-page navigation.
			sessionSlotSwitchInFlight.current = true;

			try {
				sessionStorage.removeItem('original-path');
			} catch {
				// sessionStorage can throw in private mode or when storage is disabled
			}

			try {
				await signOutSession();
			} catch {
				// Continue with local logout even if the mutation fails
			}

			// The server's active slot changed (or is gone); drop this tab's
			// assertion before any follow-up request, or it would be rejected
			// as a session-slot mismatch.
			expectedUserVar(undefined);

			try {
				await clearAllDatabases();
			} catch {
				// Continue with local logout even if cleanup fails
			}

			// The server removed the slot (and cleared the cookie if it was the
			// last one); the remaining slots decide where to go next.
			let remaining = 0;

			try {
				const { data } = await fetchSessionSlots();
				remaining = data?.sessionSlots.length ?? 0;
			} catch {
				// Treat an unreachable API as fully signed out
			}

			// Full-page navigation so the app re-bootstraps as the (new) active
			// user — or unauthenticated when no slots remain. This tears down the
			// whole JS context, so the (non-persisted) Apollo cache needs no
			// explicit clearing.
			window.location.href = remaining > 0 ? '/' : '/authentication';
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}
